import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Text } from '@cuemath/web-comps';
import { usePatchAgentDetails, invalidateAgents } from 'actions/support-agent';
import {
  AGENT_ACTIONS,
  CACHE_KEYS,
  ERROR_AUTO_CLEAR_MS,
  lastTeamDecisionBody,
} from '../../constants';
import { ConfirmText, BtnDanger } from './styled';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnSecondary,
  IconBtn,
  ModalErrorMsg,
} from '../../styled-comps';
import Icon from '../icon';

const PENDING_ACTIONS = {
  KEEP: 'keep',
  DELETE: 'delete',
};

const LastTeamDecisionDialog = ({ agent, onCancel, onCompleted }) => {
  const [submitError, setSubmitError] = useState(null);
  const inFlightRef = useRef(null);

  const { patch: patchAgent, isProcessing } = usePatchAgentDetails({
    onComplete: error => {
      if (error) {
        setSubmitError(typeof error === 'string' ? error : 'Action failed');
        inFlightRef.current = null;
        return;
      }
      invalidateAgents(CACHE_KEYS.AGENTS);
      inFlightRef.current = null;
      onCompleted();
    },
  });

  useEffect(() => {
    if (!submitError) return undefined;
    const t = setTimeout(() => setSubmitError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(t);
  }, [submitError]);

  const handleKeep = useCallback(() => {
    if (isProcessing || !agent) return;
    inFlightRef.current = PENDING_ACTIONS.KEEP;
    patchAgent(agent.user_id, { action: AGENT_ACTIONS.UPDATE_TEAMS, team_ids: [] });
  }, [agent, isProcessing, patchAgent]);

  const handleDelete = useCallback(() => {
    if (isProcessing || !agent) return;
    inFlightRef.current = PENDING_ACTIONS.DELETE;
    patchAgent(agent.user_id, { action: AGENT_ACTIONS.DELETE });
  }, [agent, isProcessing, patchAgent]);

  if (!agent) return null;

  const keepLabel =
    isProcessing && inFlightRef.current === PENDING_ACTIONS.KEEP
      ? 'Saving...'
      : 'Keep on general only';
  const deleteLabel =
    isProcessing && inFlightRef.current === PENDING_ACTIONS.DELETE
      ? 'Deleting...'
      : 'Delete agent entirely';

  return (
    <ModalOverlay onClick={onCancel}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <Text variant="body1Medium">Last specific team</Text>
          <IconBtn onClick={onCancel}>
            <Icon name="close" size={16} />
          </IconBtn>
        </ModalHead>
        <ModalBody>
          {submitError && <ModalErrorMsg>{submitError}</ModalErrorMsg>}
          <ConfirmText>{lastTeamDecisionBody(agent.name)}</ConfirmText>
        </ModalBody>
        <ModalFoot>
          <BtnSecondary onClick={onCancel}>Cancel</BtnSecondary>
          <BtnSecondary onClick={handleKeep} disabled={isProcessing}>
            {keepLabel}
          </BtnSecondary>
          <BtnDanger onClick={handleDelete} disabled={isProcessing}>
            {deleteLabel}
          </BtnDanger>
        </ModalFoot>
      </ModalCard>
    </ModalOverlay>
  );
};

export default LastTeamDecisionDialog;
